import type {
  AgentQuotaStatusResponse,
  GraphNode,
  GraphReadNode,
  GraphVisualizationFrame,
  SaleskoOAuthProvider
} from "@salesko/contracts";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CreditCard, HelpCircle, LogOut, Plus, Search, PanelRight, Settings, UserRound, Zap } from "lucide-react";
import { Button, Menu, type MenuItem } from "@salesko/design-system";
import type { KeyboardEvent, RefObject } from "react";
import { getAgentQuota, type StoredSaleskoSession } from "../../lib/api";
import { GoogleGlyph } from "../../components/GoogleGlyph";
import { entityMeta } from "../entityMeta";
import { detailSubtitle, initialsOf } from "../lib/detail";
import { savedFrameSearchSubtitle, shortFrameRecordId } from "../lib/serverSearch";
import { LanguageSwitcher, useI18n } from "../../i18n";
import { workspaceNavLabel } from "../i18n";
import { navItems, type WorkspaceViewId } from "../navItems";
import { initialsFor } from "./AccountSurface";

const providerLabels: Record<SaleskoOAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  linkedin: "LinkedIn"
};

type WorkspaceHeaderProps = {
  activeView: string;
  availableAuthProviders: SaleskoOAuthProvider[];
  frame: GraphVisualizationFrame;
  onAddClick: () => void;
  onOpenBilling: () => void;
  onOpenHelp: () => void;
  onOpenSettings: () => void;
  onSavedSearchSelect: (node: GraphReadNode) => void;
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSearchQueryChange: (value: string) => void;
  onSelectNode: (nodeId: string) => void;
  onSignIn: (provider: SaleskoOAuthProvider) => void;
  onSignOut: () => void;
  onViewChange: (viewId: WorkspaceViewId) => void;
  readOnly: boolean;
  savedSearchError: boolean;
  savedSearchPending: boolean;
  savedSearchResults: GraphReadNode[];
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchQuery: string;
  searchResults: GraphNode[];
  session: StoredSaleskoSession | null;
  showAddAction?: boolean;
  /** The one control that opens and closes `#workspace-detail`, in every view. */
  onToggleDetail: () => void;
  detailOpen: boolean;
};

export function WorkspaceHeader({
  activeView,
  availableAuthProviders,
  frame,
  onAddClick,
  onOpenBilling,
  onOpenHelp,
  onOpenSettings,
  onSavedSearchSelect,
  onSearchKeyDown,
  onSearchQueryChange,
  onSelectNode,
  onSignIn,
  onSignOut,
  onViewChange,
  readOnly,
  savedSearchError,
  savedSearchPending,
  savedSearchResults,
  searchInputRef,
  searchQuery,
  searchResults,
  session,
  onToggleDetail,
  detailOpen,
  showAddAction = true
}: WorkspaceHeaderProps) {
  const { t } = useI18n();
  const searchTerm = searchQuery.trim();

  return (
    <header className="workspace-header" aria-label={t.workspace.headerAriaLabel}>
      <span className="header-brand-mark" aria-hidden="true">
        <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g stroke="#bcae97" strokeWidth="1.5" strokeLinecap="round">
            <line x1="19" y1="21" x2="31.5" y2="10" />
            <line x1="19" y1="21" x2="7.5" y2="14" />
            <line x1="19" y1="21" x2="23" y2="33.5" />
          </g>
          <circle cx="31.5" cy="10" r="3.4" fill="#6ba872" />
          <circle cx="7.5" cy="14" r="3.1" fill="#d9974f" />
          <circle cx="23" cy="33.5" r="3" fill="#cba94f" />
          <circle cx="19" cy="21" r="6" fill="#4a8cb0" />
          <circle cx="19" cy="21" r="2.1" fill="#ffffff" />
        </svg>
      </span>

      <div className="search-control header-search">
        <label className="search-box">
          <Search size={18} />
          <input
            aria-autocomplete="list"
            aria-controls={searchTerm ? "graph-search-results" : undefined}
            aria-expanded={Boolean(searchTerm)}
            aria-keyshortcuts="Meta+K Control+K"
            aria-label={t.workspace.searchAriaLabel}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t.workspace.searchPlaceholder}
            ref={searchInputRef}
            role="combobox"
            value={searchQuery}
          />
          <kbd className="search-kbd" aria-hidden="true">
            ⌘K
          </kbd>
        </label>
        {searchTerm ? (
          <div className="search-results" id="graph-search-results" role="listbox" aria-label={t.workspace.searchResultsLabel}>
            <div className="search-results-head">
              <span>{t.workspace.currentCount(searchResults.length)}</span>
              <small>{savedSearchPending ? t.workspace.searchingSaved : t.workspace.savedCount(savedSearchResults.length)}</small>
            </div>
            {searchResults.length > 0 ? (
              searchResults.slice(0, 5).map((node) => {
                const meta = entityMeta[node.type];
                const Icon = meta.icon;
                return (
                  <button
                    className="search-result"
                    key={node.id}
                    onClick={() => {
                      onSelectNode(node.id);
                      onSearchQueryChange("");
                    }}
                    role="option"
                    type="button"
                  >
                    <span className={`mini-avatar mini-avatar-${meta.tone}`} aria-hidden="true">
                      {node.type === "person" ? (
                        <span className="mini-avatar-initials">{initialsOf(node.label)}</span>
                      ) : (
                        <Icon size={15} />
                      )}
                    </span>
                    <span>
                      <strong>{node.label}</strong>
                      <small>{detailSubtitle(node, frame, t)}</small>
                    </span>
                    <ChevronRight size={16} />
                  </button>
                );
              })
            ) : (
              <p className="search-empty">{t.workspace.noCurrentMatch}</p>
            )}
            {readOnly ? null : (
              <>
                <div className="search-results-head">
                  <span>{t.workspace.savedFrames}</span>
                  <small>{savedSearchError ? t.workspace.unavailable : t.workspace.canonicalMatches}</small>
                </div>
                {savedSearchResults.length > 0 ? (
                  savedSearchResults.slice(0, 5).map((node) => {
                    const meta = entityMeta[node.entityType];
                    const Icon = meta.icon;
                    return (
                      <button
                        className="search-result"
                        key={`${node.frameRecordId}:${node.id}`}
                        onClick={() => onSavedSearchSelect(node)}
                        role="option"
                        type="button"
                      >
                        <span className={`mini-avatar mini-avatar-${meta.tone}`} aria-hidden="true">
                          {node.entityType === "person" ? (
                            <span className="mini-avatar-initials">{initialsOf(node.label)}</span>
                          ) : (
                            <Icon size={15} />
                          )}
                        </span>
                        <span>
                          <strong>{node.label}</strong>
                          <small>
                            {savedFrameSearchSubtitle(node)} - {shortFrameRecordId(node.frameRecordId)}
                          </small>
                        </span>
                        <ChevronRight size={16} />
                      </button>
                    );
                  })
                ) : (
                  <p className="search-empty">
                    {savedSearchPending ? t.workspace.searchingSavedFrames : t.workspace.noSavedMatch}
                  </p>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>

      <nav className="header-nav" aria-label={t.workspace.viewsLabel}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeView === item.id;
          return (
            <button
              aria-current={active ? "page" : undefined}
              className={active ? "header-nav-item is-active" : "header-nav-item"}
              key={item.id}
              onClick={() => onViewChange(item.id)}
              type="button"
            >
              <Icon size={18} />
              <span>{workspaceNavLabel(t, item.id)}</span>
            </button>
          );
        })}
      </nav>

      {showAddAction ? (
        <Button iconLeft={<Plus size={16} />} onClick={onAddClick} size="sm" variant="secondary">
          {t.common.add}
        </Button>
      ) : null}

      <LanguageSwitcher className="workspace-language-switcher" />

      <HeaderAccountMenu
        availableAuthProviders={availableAuthProviders}
        onOpenBilling={onOpenBilling}
        onOpenHelp={onOpenHelp}
        onOpenSettings={onOpenSettings}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        session={session}
      />

      {/* Last child on purpose: the detail panel sits at the workspace's right
          edge, so its only control belongs at the header's right end. */}
      <button
        id="workspace-detail-trigger"
        className="icon-button workspace-detail-trigger"
        aria-label={t.workspace.detailPanelToggleAriaLabel}
        title={t.workspace.detailPanelToggleAriaLabel}
        type="button"
        aria-expanded={detailOpen}
        aria-controls="workspace-detail"
        onClick={onToggleDetail}
      ><PanelRight size={18} /></button>
    </header>
  );
}

type HeaderAccountMenuProps = {
  availableAuthProviders: SaleskoOAuthProvider[];
  onOpenBilling: () => void;
  onOpenHelp: () => void;
  onOpenSettings: () => void;
  onSignIn: (provider: SaleskoOAuthProvider) => void;
  onSignOut: () => void;
  session: StoredSaleskoSession | null;
};

function HeaderAccountMenu({
  availableAuthProviders,
  onOpenBilling,
  onOpenHelp,
  onOpenSettings,
  onSignIn,
  onSignOut,
  session
}: HeaderAccountMenuProps) {
  const { t } = useI18n();
  const displayName = session?.name ?? session?.email ?? t.common.account;
  // Daily Generic Agent allowance shown next to the name; read-only, so opening
  // the menu never spends quota. Keyed by user so a tenant switch refetches.
  const quotaQuery = useQuery({
    queryKey: ["agent-quota", session?.userId ?? null],
    queryFn: getAgentQuota,
    enabled: Boolean(session),
    staleTime: 60_000,
    retry: 1
  });

  const trigger = (
    <button className="header-account-trigger" type="button" aria-label={t.workspace.accountMenuLabel} aria-haspopup="menu">
      <span className="header-avatar" aria-hidden="true">
        {session ? initialsFor(displayName) : <UserRound size={16} />}
      </span>
      <span className="header-account-label">{session ? t.common.account : t.common.signIn}</span>
      <ChevronDown size={15} />
    </button>
  );

  const signedOutItems: MenuItem[] =
    availableAuthProviders.length > 0
      ? availableAuthProviders.map((provider) => ({
          label: t.common.signInWith(providerLabels[provider]),
          icon: provider === "google" ? <GoogleGlyph size={16} /> : <UserRound size={16} />,
          onClick: () => onSignIn(provider)
        }))
      : [{ label: t.workspace.noProviderConfigured, disabled: true }];
  signedOutItems.push({ divider: true }, { label: t.billing.viewPlans, icon: <CreditCard size={16} />, onClick: onOpenBilling });

  const items: MenuItem[] = session
    ? [
        {
          label: (
            <span
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%" }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
              <AgentQuotaPill quota={quotaQuery.data} loading={quotaQuery.isPending && quotaQuery.fetchStatus !== "idle"} />
            </span>
          ),
          disabled: true
        },
        { divider: true },
        { label: t.common.settings, icon: <Settings size={16} />, onClick: onOpenSettings },
        { label: t.billing.viewPlans, icon: <CreditCard size={16} />, onClick: onOpenBilling },
        { label: t.common.help, icon: <HelpCircle size={16} />, onClick: onOpenHelp },
        { divider: true },
        { label: t.common.signOut, icon: <LogOut size={16} />, onClick: onSignOut, tone: "danger" }
      ]
    : signedOutItems;

  return <Menu align="end" items={items} trigger={trigger} width={260} />;
}

/** Small pill rendered next to the account name showing today's remaining
 * Generic Agent messages. Hidden when signed out / on error (quota null);
 * turns red as the daily allowance runs low or hits zero. */
function AgentQuotaPill({ quota, loading }: { quota: AgentQuotaStatusResponse | null | undefined; loading: boolean }) {
  const { t } = useI18n();
  if (loading) {
    return <span style={{ fontSize: 11, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>···</span>;
  }
  if (!quota) {
    return null;
  }

  const unlimited = quota.limit === null || quota.remaining === null;
  const remaining = quota.remaining ?? 0;
  const limit = quota.limit ?? 0;
  const depleted = !unlimited && remaining === 0;
  const low = !unlimited && !depleted && remaining <= Math.max(1, Math.floor(limit * 0.15));
  const tone = depleted || low ? "var(--red-600)" : "var(--text-muted)";
  const title = unlimited
    ? t.workspace.quotaUnlimitedTooltip
    : t.workspace.quotaRemaining(remaining, limit);

  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        flex: "none",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        color: tone,
        background: "var(--surface-hover)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 999,
        padding: "3px 7px",
        fontVariantNumeric: "tabular-nums"
      }}
    >
      <Zap size={11} aria-hidden="true" />
      {unlimited ? t.workspace.quotaUnlimited : `${remaining}/${limit}`}
    </span>
  );
}
